<template>
    <div>
        <ProductCustomisationVariationAlphanumericInput
            v-if="variationType.is_alphanumeric"
            :variation-type="variationType"
            @update:selected="selectVariation($event.ids, $event.isText, $event.inStock)"
        />
        <template v-else>
            <ProductCustomisationImageUpload
                v-if="variationType.variations[0].customer_image_enabled"
                @imageUpdated="imageUpdated"
            />

            <div
                class="label mb-2"
            >
                {{ variationTypeName }}
                <span
                    v-if="variationType.price !== false"
                    class="font-bold text-grey-darker"
                >
                    <OfferPrice
                        :offer-name="variationType.offer_name"
                        :offer-price="variationType.offer_price"
                        :price="variationType.price"
                    />
                </span>
                <span v-if="maxTextChars">(max {{ maxTextChars }} characters)</span>
            </div>

            <div
                v-if="isTextVariationType"
            >
                <template v-if="maxTextChars > 0">
                    <Input
                        v-model="text"
                        type="text"
                        class="mb-3"
                        placeholder="e.g. Happy Birthday Hannah"
                        :maxlength="parseFloat(maxTextChars)"
                        @input="selectVariation(variationType.variations[0].id, true)"
                        @focus="showCustomisationCanvas"
                    />

                    <Input
                        v-if="variationType.variations[0].customer_image_enabled || variationType.variations[0].is_text_line_2_enabled"
                        v-model="textLine2"
                        :label="variationType.variations[0].text_line_2_label"
                        type="text"
                        :maxlength="parseFloat(maxTextChars)"
                        class="mb-3"
                        :placeholder="sampleTextLine2"
                        @input="selectVariation(variationType.variations[0].id, true)"
                        @focus="showCustomisationCanvas"
                    />
                </template>

                <template v-if="fonts.length > 1">
                    <FontSelect
                        :fonts="fonts"
                        :label="variationType.font_label"
                        :text="text || sampleTextLine1"
                        :selected-font="selectedFont"
                        @select="selectFont"
                    />
                </template>

            </div>

            <div
                v-else
                class="mb-2 grid grid-cols-4 md:grid-cols-10 gap-2"
            >
                <div
                    v-for="(variation, index) in variationType.variations"
                    :key="index"
                    :class="{ 'button--active': selectedVariationIds.includes(variation.id), 'button--disabled': !variation.is_in_stock, 'md:col-span-2': variation.name.length > 3}"
                    class="button flex flex-col items-center text-center"
                    @click="selectVariation(variation.id, false, variation.is_in_stock)"
                >
                    <img
                        v-if="variation.image.sizes.small"
                        :src="variation.image.sizes.small"
                        alt=""
                        class="block"
                    >
                    <span
                        v-if="!variationType.hide_variation_names"
                        class="text-primary hidden md:block"
                    >
                        {{ variation.name }}
                    </span>
                    <span v-if="variation.price !== 0 && variationType.price === false">
                        <OfferPrice
                            :offer-name="variation.offer_name"
                            :offer-price="variation.offer_price"
                            :price="variation.price"
                        />
                    </span>
                    <div
                        v-if="!variation.is_in_stock"
                        class="mt-auto"
                    >
                        <div class="text-2025-hot-pink uppercase text-xs font-semibold">
                            Sold out
                        </div>
                        <div
                            class="text-primary cursor-pointer text-xs"
                            @click="showStockNotification"
                        >
                            Get notified when it's back in stock
                        </div>
                    </div>

                </div>
            </div>

        </template>

    </div>
</template>

<script>
import Input from '~/components/Input';
import ProductCustomisationImageUpload from '~/components/ProductCustomisationImageUpload';
import OfferPrice from '~/components/OfferPrice';
import ProductCustomisationVariationAlphanumericInput
from '@/components/ProductCustomisationVariationAlphanumericInput.vue';
import FontSelect from './FontSelect.vue';

export default {

    components: {
        FontSelect,
        ProductCustomisationVariationAlphanumericInput,
        Input,
        ProductCustomisationImageUpload,
        OfferPrice,
    },

    props: {
        variationType: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            selectedVariationIds: [],
            rawText: '',
            rawTextLine2: '',
            cleanPattern: /[^\w\s.,!?<>|£$%^&*()'";:\\[\]{}\-=_+`’~]+/g,
            hasModifiedText: false,
        };
    },

    computed: {
        selectedFont: {
            get() {
                return this.$store.getters['product/fontId'];
            },
            set(fontId) {
                this.$store.dispatch('product/setFontId', fontId);
            },
        },
        fonts: {
            get() {
                return this.$store.getters['product/fonts'];
            },
        },
        variationTypeName() {
            return this.variationType.name;
        },
        isTextVariationType() {
            return this.variationType.text;
        },
        maxTextChars() {
            return this.isTextVariationType && this.variationType.variations[0].text_max_chars;
        },
        isRequired() {
            return this.variationType.required;
        },
        isOptional() {
            return !this.isRequired();
        },
        isMultiple() {
            return this.variationType.multiple || this.variationType.is_alphanumeric;
        },
        text: {
            get() {
                // return this.rawText || (this.hasModifiedText ? '' : this.sampleTextLine1);
                return this.rawText;
            },
            set(val) {
                this.hasModifiedText = true;
                this.rawText = val.substring(0, this.maxTextChars)
                    .replace(this.cleanPattern, '');
            },
        },
        textLine2: {
            get() {
                return this.rawTextLine2 || (this.hasModifiedText ? '' : this.sampleTextLine2);
            },
            set(val) {
                this.hasModifiedText = true;
                this.rawTextLine2 = val.substring(0, this.maxTextChars)
                    .replace(this.cleanPattern, '');
            },
        },
        sampleTextLine1() {
            return this.variationType.variations[0].sample_text_line_1 ?? '';
        },
        sampleTextLine2() {
            return this.variationType.variations[0].sample_text_line_2 ?? '';
        },
        inStockVariations() {
            return this.variationType.variations.filter((variation) => variation.is_in_stock);
        },
    },

    mounted() {
        if (this.isTextVariationType && this.sampleTextLine1) {
            this.selectVariation(this.variationType.variations[0].id, true);
        }
    },

    methods: {
        showCustomisationCanvas() {
            this.$store.dispatch('product/setShowProductCustomisationImage', true);
        },
        hideCustomisationCanvas() {
            this.$store.dispatch('product/setShowProductCustomisationImage', false);
        },
        selectVariation(id, isText = false, inStock = true) {
            if (!inStock) {
                return;
            }

            let deselect = false;

            // if this is for addons, allow de-selection
            // if this is not for addons, don't allow multiple selections
            if (this.variationType.is_alphanumeric) {
                this.selectedVariationIds = id;
                if (this.selectedVariationIds.length === 0) {
                    deselect = true;
                }
            } else if ((!isText && this.selectedVariationIds.includes(id)) || (this.maxTextChars > 0 && isText && this.text.length === 0)) {
                this.selectedVariationIds = this.selectedVariationIds.filter((varId) => varId !== id);
                deselect = true;
            } else if (this.isMultiple) {
                this.selectedVariationIds = [...this.selectedVariationIds, id];
            } else {
                this.selectedVariationIds = [id];
            }
            this.$emit('selected', {
                variationType: this.variationType,
                variationIds: this.selectedVariationIds,
                text: isText ? this.text.substring(0, this.maxTextChars) : null,
                textLine2: isText ? this.textLine2.substring(0, this.maxTextChars) : null,
                // the just-now selected id
                newSelectionId: Array.isArray(id) ? id.slice(-1)[0] : id,
                // the price of all selected variations for this type
                // sorted as productVariationsCompound sorts them, so the
                // character charged in full is the one the API charges in full
                price: [...this.selectedVariationIds]
                    .sort()
                    .map((selected) => this.variationType.variations.find((variation) => variation.id === selected))
                    .reduce((acc, variation, index) => acc + this.variationPrice(variation, index), 0),
                deselect,
            });
        },
        // On an alphanumeric type with tiered pricing, the first character
        // costs its own price and the rest cost the additional price. The API
        // charges the same way.
        variationPrice(variation, index) {
            if (this.variationType.is_alphanumeric && index > 0 && variation.additional_character_price !== undefined) {
                return parseFloat(variation.additional_character_offer_price !== null
                    ? variation.additional_character_offer_price
                    : variation.additional_character_price);
            }

            return parseFloat(variation.offer_price !== null ? variation.offer_price : variation.price);
        },
        priceSign(price) {
            if (price > 0) {
                return '+';
            }

            if (price < 0) {
                return '-';
            }

            return '';
        },
        price(price) {
            if (price === 0) {
                return 'FREE';
            }

            return `${this.priceSign(price)}£${Math.abs(price)
                .toFixed(2)}`;
        },
        imageUpdated(event) {
            this.$emit('imageUpdated', event);

            if (this.maxTextChars) {
                this.selectVariation(this.variationType.variations[0].id, true);
            }
        },
        showStockNotification() {
            this.$emit('showStockNotification');
        },
        selectFont(font) {
            this.selectedFont = font.id;
            this.showCustomisationCanvas();
        },
    },

};
</script>

<style lang="scss" scoped>
.label {
    @apply text-grey-darker;
}

.button {
    @apply text-sm cursor-pointer transition-fast select-none;

    img {
        @apply rounded-lg border-4 border-transparent;
    }

    &--active {
        img {
            @apply border-primary;
        }
    }

    @media (min-width: config("theme.screens.md")) {
        &:hover {
            @apply bg-primary text-white;
        }
    }

    &--disabled {
        @apply text-grey;
        cursor: default;

        span, img {
            opacity: 0.35;
        }

        &:hover {
            @apply bg-transparent text-grey;
        }
    }
}
</style>
